import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  getWhatsapp,
  createWhatsapp,
  updateWhatsapp,
  restartWhatsapp,
} from "../../services/whatsapp.api";
import toastError from "../../errors/toastError";
import WabaEmbeddedSignup from "./WabaEmbeddedSignup";

/**
 * Modal DEDICADO para la conexión oficial de WhatsApp (WABA / Meta Cloud API).
 * Separado del modal de Baileys porque el tratamiento es distinto:
 * credenciales Graph en vez de QR, estado derivado (no manual), y sin automatización
 * hasta resolver plantillas HSM. Ver 0362 §10.
 *
 * "Conectar y validar" persiste las credenciales y dispara la validación en el backend
 * (isAlive + suscripción del webhook). La confirmación de RECEPCIÓN se hace luego en la
 * tarjeta (enviar un mensaje real al número) — este modal solo cierra el envío.
 */

const EMPTY = {
  name: "",
  phoneNumberId: "",
  wabaId: "",
  businessId: "",
  verifyToken: "",
  wabaToken: "",
  appSecret: "",
  appId: "",
  isDefault: false,
};

const FIELDS = [
  { key: "phoneNumberId", label: "Phone Number ID", helper: "WhatsApp > API Setup" },
  { key: "wabaId", label: "WABA ID", helper: "WhatsApp Business Account ID" },
  { key: "businessId", label: "Business ID", helper: "Meta Business (Portfolio) ID" },
  { key: "verifyToken", label: "Verify Token (webhook)", helper: "El mismo que registrarás en Meta" },
];

const WhatsAppOficialModal = ({ open, onClose, whatsAppId, onSave }) => {
  const [values, setValues] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open || !whatsAppId) {
        setValues(EMPTY);
        return;
      }
      try {
        setLoading(true);
        const { data } = await getWhatsapp(whatsAppId, { session: 0 });
        if (!mounted) return;
        setValues({
          name: data.name || "",
          phoneNumberId: data.phoneNumberId || "",
          wabaId: data.wabaId || "",
          businessId: data.businessId || "",
          verifyToken: data.verifyToken || "",
          wabaToken: data.wabaToken || "",
          appSecret: data.appSecret || "",
          appId: data.appId || "",
          isDefault: !!data.isDefault,
        });
      } catch (err) {
        if (mounted) toastError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, whatsAppId]);

  const setField = (key) => (e) =>
    setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleClose = useCallback(() => {
    setValues(EMPTY);
    setSaving(false);
    onClose();
  }, [onClose]);

  const handleConnect = async () => {
    const faltantes = [];
    if (!values.name.trim()) faltantes.push("Nombre");
    if (!values.phoneNumberId.trim()) faltantes.push("Phone Number ID");
    if (!values.wabaId.trim()) faltantes.push("WABA ID");
    if (!values.businessId.trim()) faltantes.push("Business ID");
    if (!values.verifyToken.trim()) faltantes.push("Verify Token");
    if (!values.wabaToken.trim()) faltantes.push("Token de acceso");
    if (faltantes.length > 0) {
      toast.error(`❌ Faltan credenciales: ${faltantes.join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        provider: "waba",
        status: "OPENING",
        isDefault: values.isDefault,
        phoneNumberId: values.phoneNumberId.trim(),
        wabaId: values.wabaId.trim(),
        businessId: values.businessId.trim(),
        verifyToken: values.verifyToken.trim(),
        wabaToken: values.wabaToken.trim(),
        appSecret: values.appSecret.trim(),
        appId: values.appId.trim(),
      };

      if (whatsAppId) {
        // Editar: persistir y re-validar (restart dispara initSession → valida + re-suscribe).
        await updateWhatsapp(whatsAppId, payload);
        await restartWhatsapp(whatsAppId);
      } else {
        // Crear: el store dispara StartWhatsAppSession → initSession (valida + suscribe).
        await createWhatsapp(payload);
      }

      toast.success(
        "✅ Conexión guardada. Validando credenciales con Meta… Para completar, envía un mensaje de prueba al número (ver aviso en la tarjeta)."
      );
      if (onSave) onSave();
      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {whatsAppId ? "Editar conexión" : "Conectar"} WhatsApp Oficial — Meta Cloud API
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {!whatsAppId && (
              <WabaEmbeddedSignup
                onConnected={() => {
                  if (onSave) onSave();
                  handleClose();
                }}
              />
            )}

            <Grid item xs={12}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Ingresa las credenciales de tu App / WABA (Meta for Developers →
                WhatsApp → API Setup). Al conectar, se validan con Meta y se suscribe el
                webhook automáticamente.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Nombre de la conexión"
                variant="outlined"
                margin="dense"
                fullWidth
                required
                autoFocus
                value={values.name}
                onChange={setField("name")}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mt={1}>
                <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#1877F2" }}>
                  🔑 Credenciales de WhatsApp Cloud API (Meta)
                </span>
              </Box>
              <Divider style={{ marginTop: 6 }} />
            </Grid>

            {FIELDS.map((f) => (
              <Grid item xs={12} sm={6} key={f.key}>
                <TextField
                  label={f.label}
                  helperText={f.helper}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  value={values[f.key]}
                  onChange={setField(f.key)}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                label="Token de acceso (System User)"
                helperText="Token permanente de envío (Bearer). Se guarda en el servidor."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.wabaToken}
                onChange={setField("wabaToken")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="App Secret (opcional)"
                helperText="Valida la firma del webhook (X-Hub-Signature-256). Recomendado en producción."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.appSecret}
                onChange={setField("appSecret")}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="App ID (opcional)"
                helperText="Meta > App > Configuración > Información básica. Necesario para crear plantillas con imagen de encabezado."
                variant="outlined"
                margin="dense"
                fullWidth
                value={values.appId}
                onChange={setField("appId")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={values.isDefault}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, isDefault: e.target.checked }))
                    }
                  />
                }
                label="Predeterminada"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleConnect}
          variant="contained"
          color="primary"
          disabled={saving || loading}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Conectar y validar"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppOficialModal;
